import React, { useState } from "react";
import { API_ENDPOINTS } from "../../../config/api";
import { useAuth } from "../../../context/AuthContext";

// ── Styles ────────────────────────────────────────────────────────────────────
const s = {
  page: {
    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    color: "#1a1d23",
  },
  card: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    overflow: "hidden",
    marginBottom: "1.25rem",
  },
  cardHead: {
    padding: "0.75rem 1.125rem",
    borderBottom: "1px solid #e5e7eb",
    background: "#fafafa",
    fontSize: 13,
    fontWeight: 600,
    color: "#1a1d23",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  cardBody: { padding: "1rem 1.125rem" },
  kpiCard: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    padding: "1rem 1.125rem",
  },
  kpiLabel: {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: "0.07em",
    textTransform: "uppercase",
    color: "#6b7280",
    marginBottom: 6,
  },
  kpiValue: { fontSize: 22, fontWeight: 700, lineHeight: 1, color: "#1a1d23" },
  kpiSub: { fontSize: 12, color: "#9ca3af", marginTop: 5 },
  fieldLabel: {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    color: "#6b7280",
    marginBottom: 2,
  },
  fieldValue: { fontSize: 13, color: "#1a1d23" },
  sectionLabel: {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: "0.07em",
    textTransform: "uppercase",
    color: "#6b7280",
  },
  pill: (variant) => {
    const map = {
      green: { background: "#F0FDF4", color: "#166534" },
      amber: { background: "#FFFBEB", color: "#92400E" },
      blue: { background: "#EFF6FF", color: "#1E40AF" },
      gray: { background: "#F9FAFB", color: "#4B5563", border: "1px solid #e5e7eb" },
      red: { background: "#FEF2F2", color: "#991B1B" },
    };
    return {
      fontSize: 11,
      fontWeight: 600,
      padding: "2px 9px",
      borderRadius: 20,
      whiteSpace: "nowrap",
      display: "inline-block",
      ...(map[variant] || map.gray),
    };
  },
  btn: {
    fontSize: 11,
    fontWeight: 500,
    padding: "5px 12px",
    borderRadius: 6,
    border: "1px solid #d1d5db",
    background: "transparent",
    color: "#374151",
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  btnGreen: {
    fontSize: 11,
    fontWeight: 500,
    padding: "4px 12px",
    borderRadius: 6,
    border: "1px solid #BBF7D0",
    background: "transparent",
    color: "#166534",
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  btnPrimary: {
    fontSize: 12,
    fontWeight: 600,
    padding: "5px 14px",
    borderRadius: 6,
    border: "none",
    background: "#1E40AF",
    color: "#fff",
    cursor: "pointer",
  },
  btnLink: {
    fontSize: 11,
    color: "#6b7280",
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: 0,
    textDecoration: "underline",
  },
  th: {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "#6b7280",
    padding: "8px 12px",
    textAlign: "left",
    borderBottom: "1px solid #e5e7eb",
    whiteSpace: "nowrap",
    background: "#fafafa",
  },
  td: {
    padding: "9px 12px",
    borderBottom: "1px solid #f3f4f6",
    color: "#1a1d23",
    fontSize: 13,
    verticalAlign: "middle",
    whiteSpace: "nowrap",
  },
  spinner: {
    display: "inline-block",
    width: 12,
    height: 12,
    border: "2px solid #e5e7eb",
    borderTopColor: "#6b7280",
    borderRadius: "50%",
    animation: "spin 0.7s linear infinite",
    verticalAlign: "middle",
    marginRight: 5,
  },
  input: {
    fontSize: 13,
    padding: "5px 9px",
    border: "1px solid #d1d5db",
    borderRadius: 6,
    color: "#1a1d23",
    background: "#fff",
    width: 80,
    outline: "none",
  },
};

// ── Sub-components ────────────────────────────────────────────────────────────
const Spinner = () => (
  <>
    <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    <span style={s.spinner} />
  </>
);

const Pill = ({ variant, children }) => <span style={s.pill(variant)}>{children}</span>;

const FieldRow = ({ label, value }) => (
  <div>
    <div style={s.fieldLabel}>{label}</div>
    <div style={s.fieldValue}>{value ?? "—"}</div>
  </div>
);

const InfoRow = ({ label, value }) => (
  <div
    style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "6px 0",
      borderBottom: "1px solid #f3f4f6",
      fontSize: 13,
    }}
  >
    <span style={{ color: "#6b7280" }}>{label}</span>
    <span style={{ fontWeight: 500 }}>{value}</span>
  </div>
);

// ── Helpers ───────────────────────────────────────────────────────────────────
const TT_OFFSET_MS = 4 * 60 * 60 * 1000;

const formatCurrency = (n) =>
  new Intl.NumberFormat("en-TT", {
    style: "currency",
    currency: "TTD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(n || 0);

const toTrinidadAdjustedDate = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return new Date(parsed.getTime() + TT_OFFSET_MS);
};

const formatDate = (date) => {
  if (!date) return "Not set";
  const adjusted = toTrinidadAdjustedDate(date);
  if (!adjusted) return "Not set";
  return adjusted.toLocaleDateString("en-US", {
    timeZone: "America/Port_of_Spain",
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const getTrinidadDateParts = (value) => {
  const adjusted = toTrinidadAdjustedDate(value);
  if (!adjusted) return null;
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Port_of_Spain",
    year: "numeric",
    month: "numeric",
    day: "numeric",
  }).formatToParts(adjusted);
  const get = (type) => Number(parts.find((p) => p.type === type)?.value || 0);
  return { year: get("year"), month: get("month"), day: get("day") };
};

// ── Main component ────────────────────────────────────────────────────────────
const OneTimeRentalFinancials = ({ property, onPropertyUpdated }) => {
  const [editingRate, setEditingRate] = useState(false);
  const [rateInput, setRateInput] = useState("");
  const [savingRate, setSavingRate] = useState(false);
  const [markPaidModal, setMarkPaidModal] = useState(false);
  const [markPaidDate, setMarkPaidDate] = useState("");
  const [markPaidNotes, setMarkPaidNotes] = useState("");
  const [markPaidError, setMarkPaidError] = useState("");
  const [submittingMarkPaid, setSubmittingMarkPaid] = useState(false);
  const { getAuthHeaders } = useAuth();

  if (!property) {
    return (
      <div style={{ textAlign: "center", padding: "2rem", fontSize: 14, color: "#6b7280" }}>
        Property data not available
      </div>
    );
  }

  // ── Derived data ──────────────────────────────────────────────────────────
  const monthlyRent = property.pricing?.rent || 0;
  const deposit = property.pricing?.deposit || 0;
  const commission = property.pricing?.rentalCommission || {};
  const placementInfo = property.placementInfo || {};
  const tenantInfo = placementInfo.tenantInfo || {};
  const ongoingRate = commission.ongoingRate != null ? commission.ongoingRate : 5;

  const leaseStartParts = getTrinidadDateParts(tenantInfo.leaseStartDate);
  const placementRecordedPaid = Boolean(commission.isPaid);

  const placementAmount = commission.amount ?? 0;
  const placementPaid = placementRecordedPaid ? placementAmount : 0;
  const placementPending = placementRecordedPaid ? 0 : placementAmount;
  const totalCommissionToDate = placementPaid + placementPending;

  const getCommissionDisplay = () => {
    if (commission.type === "first_month_rent") return "First Month's Rent (placement)";
    if (commission.type === "percentage") return `${commission.rate}% of Annual Rent`;
    if (commission.type === "flat_fee") return "Flat Fee";
    return "Not specified";
  };

  const getStatusPill = () => {
    const status = property.status;
    if (status === "rented") return <Pill variant="green">Tenant Placed</Pill>;
    if (status === "pending") return <Pill variant="amber">Application Pending</Pill>;
    if (status === "available") return <Pill variant="blue">Available</Pill>;
    if (status === "withdrawn") return <Pill variant="gray">Withdrawn</Pill>;
    return <Pill variant="gray">Unknown</Pill>;
  };

  // ── Handlers ──────────────────────────────────────────────────────────────
  const openMarkPaidModal = () => {
    if (!property._id || !leaseStartParts || placementRecordedPaid) return;
    setMarkPaidDate(new Date().toISOString().split("T")[0]);
    setMarkPaidNotes("");
    setMarkPaidError("");
    setMarkPaidModal(true);
  };

  const handleSubmitMarkPaid = async () => {
    setSubmittingMarkPaid(true);
    setMarkPaidError("");
    try {
      const res = await fetch(API_ENDPOINTS.properties.markRentPaid(property._id), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "ngrok-skip-browser-warning": "true",
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          month: leaseStartParts.month,
          year: leaseStartParts.year,
          ...(markPaidDate ? { paidAt: `${markPaidDate}T12:00:00.000Z` } : {}),
          ...(markPaidNotes.trim() ? { notes: markPaidNotes.trim() } : {}),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || "Failed to mark placement paid");
      onPropertyUpdated?.(data.data);
      setMarkPaidModal(false);
    } catch (err) {
      setMarkPaidError(err.message);
    } finally {
      setSubmittingMarkPaid(false);
    }
  };

  const handleSaveRate = async () => {
    const parsed = parseFloat(rateInput);
    if (isNaN(parsed) || parsed < 0 || parsed > 100 || !property._id) return;
    setSavingRate(true);
    try {
      const res = await fetch(API_ENDPOINTS.properties.update(property._id), {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "ngrok-skip-browser-warning": "true",
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          pricing: {
            ...property.pricing,
            rentalCommission: { ...property.pricing?.rentalCommission, ongoingRate: parsed },
          },
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || "Failed to update commission rate");
      onPropertyUpdated?.(data.data || data);
      setEditingRate(false);
    } catch (err) {
      console.error(err);
    } finally {
      setSavingRate(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={s.page}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>

      {/* ── KPI strip ── */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
          gap: 10,
          marginBottom: "1.25rem",
        }}
      >
        <div style={s.kpiCard}>
          <div style={s.kpiLabel}>Monthly rent</div>
          <div style={{ ...s.kpiValue, color: "#1E40AF" }}>{formatCurrency(monthlyRent)}</div>
          <div style={s.kpiSub}>per month</div>
        </div>

        <div style={s.kpiCard}>
          <div style={s.kpiLabel}>Security deposit</div>
          <div style={{ ...s.kpiValue, color: "#0F6E56" }}>{formatCurrency(deposit)}</div>
          <div style={s.kpiSub}>required upfront</div>
        </div>

        <div style={s.kpiCard}>
          <div style={s.kpiLabel}>Placement commission</div>
          <div style={{ ...s.kpiValue, color: "#166534" }}>
            {placementRecordedPaid ? formatCurrency(placementAmount) : "—"}
          </div>
          <div style={s.kpiSub}>{getCommissionDisplay()}</div>
        </div>

        <div style={s.kpiCard}>
          <div style={s.kpiLabel}>Commission rate</div>
          {editingRate ? (
            <div style={{ marginTop: 4 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <input
                  style={s.input}
                  type="number"
                  min="0"
                  max="100"
                  step="0.5"
                  value={rateInput}
                  autoFocus
                  onChange={(e) => setRateInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSaveRate();
                    if (e.key === "Escape") setEditingRate(false);
                  }}
                />
                <span style={{ fontSize: 13, color: "#6b7280" }}>%</span>
              </div>
              <div style={{ fontSize: 11, color: "#9ca3af", margin: "4px 0 8px" }}>
                ongoing rate
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                <button style={s.btnPrimary} disabled={savingRate} onClick={handleSaveRate}>
                  {savingRate ? (
                    <>
                      <Spinner />
                      Saving…
                    </>
                  ) : (
                    "Save"
                  )}
                </button>
                <button style={s.btn} disabled={savingRate} onClick={() => setEditingRate(false)}>
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <>
              <div style={{ ...s.kpiValue, color: "#92400E" }}>{ongoingRate}%</div>
              <div style={{ ...s.kpiSub, display: "flex", alignItems: "center", gap: 6 }}>
                ongoing rate
                <button
                  style={s.btnLink}
                  onClick={() => {
                    setRateInput(String(ongoingRate));
                    setEditingRate(true);
                  }}
                >
                  Edit
                </button>
              </div>
            </>
          )}
        </div>

        <div style={s.kpiCard}>
          <div style={s.kpiLabel}>Property status</div>
          <div style={{ marginTop: 6 }}>{getStatusPill()}</div>
          <div style={s.kpiSub}>current status</div>
        </div>
      </div>

      {/* ── Commission summary ── */}
      {(placementInfo.tenantPlaced || placementAmount > 0) && (
        <div style={s.card}>
          <div style={s.cardHead}>Commission earned to date</div>
          <div style={s.cardBody}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                gap: "1rem",
              }}
            >
              <div>
                <div style={s.sectionLabel}>Placement paid</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: "#166534", marginTop: 4 }}>
                  {formatCurrency(placementPaid)}
                </div>
              </div>
              <div>
                <div style={s.sectionLabel}>Placement pending</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: "#92400E", marginTop: 4 }}>
                  {formatCurrency(placementPending)}
                </div>
              </div>
              <div>
                <div style={s.sectionLabel}>Total commission</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: "#1a1d23", marginTop: 4 }}>
                  {formatCurrency(totalCommissionToDate)}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Commission structure + placement information ── */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
          gap: "1rem",
          marginBottom: "1.25rem",
        }}
      >
        {/* Commission structure */}
        <div style={{ ...s.card, marginBottom: 0 }}>
          <div style={s.cardHead}>Commission structure</div>
          <div style={s.cardBody}>
            <InfoRow label="Commission type" value={getCommissionDisplay()} />
            <InfoRow
              label="Placement amount"
              value={
                <span style={{ color: "#166534" }}>
                  {commission.isPaid ? formatCurrency(commission.amount) : "Pending"}
                </span>
              }
            />
            <InfoRow
              label="Ongoing rate"
              value={<span style={{ color: "#92400E" }}>{ongoingRate}%</span>}
            />
            <InfoRow
              label="Paid by"
              value={
                <span style={{ textTransform: "capitalize" }}>
                  {commission.paidBy || "Not specified"}
                </span>
              }
            />
            <InfoRow
              label="Payment status"
              value={
                commission.isPaid ? (
                  <Pill variant="green">Paid</Pill>
                ) : (
                  <Pill variant="amber">Pending</Pill>
                )
              }
            />
            {commission.isPaid && placementInfo.commissionPaidDate && (
              <InfoRow label="Paid on" value={formatDate(placementInfo.commissionPaidDate)} />
            )}
          </div>
        </div>

        {/* Placement information */}
        <div style={{ ...s.card, marginBottom: 0 }}>
          <div style={s.cardHead}>Placement information</div>
          <div style={s.cardBody}>
            <InfoRow
              label="Tenant placed"
              value={
                placementInfo.tenantPlaced ? (
                  <Pill variant="green">Yes</Pill>
                ) : (
                  <Pill variant="gray">No</Pill>
                )
              }
            />
            {placementInfo.applicationPending && (
              <InfoRow label="Application" value={<Pill variant="amber">Under Review</Pill>} />
            )}
            {placementInfo.applicationDate && (
              <InfoRow label="Application date" value={formatDate(placementInfo.applicationDate)} />
            )}
            {placementInfo.leaseSignedDate && (
              <InfoRow label="Lease signed" value={formatDate(placementInfo.leaseSignedDate)} />
            )}
            {placementInfo.tenantMoveInDate && (
              <InfoRow label="Move-in date" value={formatDate(placementInfo.tenantMoveInDate)} />
            )}
            {property.pricing?.utilities && (
              <InfoRow
                label="Utilities"
                value={
                  <span style={{ textTransform: "capitalize" }}>
                    {property.pricing.utilities.replace("_", " ")}
                  </span>
                }
              />
            )}
          </div>
        </div>
      </div>

      {/* ── Tenant information ── */}
      {placementInfo.tenantPlaced && (
        <div style={s.card}>
          <div style={s.cardHead}>Linked tenant information</div>
          <div style={s.cardBody}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
                gap: "0.875rem",
              }}
            >
              <FieldRow
                label="Tenant name"
                value={
                  tenantInfo.tenantId?.personalInfo?.firstName &&
                  tenantInfo.tenantId?.personalInfo?.lastName
                    ? `${tenantInfo.tenantId.personalInfo.firstName} ${tenantInfo.tenantId.personalInfo.lastName}`
                    : tenantInfo.tenantName || "Not specified"
                }
              />
              <FieldRow
                label="Email"
                value={tenantInfo.tenantId?.personalInfo?.email || "Not specified"}
              />
              <FieldRow
                label="Phone"
                value={
                  tenantInfo.tenantId?.personalInfo?.phone ||
                  tenantInfo.tenantContact ||
                  "Not specified"
                }
              />
              <FieldRow
                label="Monthly rent"
                value={
                  <span style={{ color: "#166534", fontWeight: 600 }}>
                    {formatCurrency(tenantInfo.monthlyRent)}
                  </span>
                }
              />
              <FieldRow label="Lease start" value={formatDate(tenantInfo.leaseStartDate)} />
              <FieldRow label="Lease end" value={formatDate(tenantInfo.leaseEndDate)} />
            </div>
          </div>
        </div>
      )}

      {/* ── Placement settlement ── */}
      {placementInfo.tenantPlaced && (
        <div style={s.card}>
          <div style={s.cardHead}>Placement settlement</div>
          <div style={s.cardBody}>
            <p
              style={{ fontSize: 12, color: "#6b7280", marginBottom: "0.875rem", lineHeight: 1.6 }}
            >
              One-time rentals only pay a placement commission for the first month. After placement
              is marked paid, this property no longer accrues monthly commission.
            </p>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 12,
              }}
            >
              <div>
                <div style={s.fieldLabel}>Placement month</div>
                <div style={s.fieldValue}>{formatDate(tenantInfo.leaseStartDate)}</div>
              </div>
              <div>
                <div style={s.fieldLabel}>Placement status</div>
                <div style={{ marginTop: 4 }}>
                  {placementRecordedPaid ? (
                    <Pill variant="green">
                      Paid{" "}
                      {placementInfo.commissionPaidDate
                        ? `· ${formatDate(placementInfo.commissionPaidDate)}`
                        : ""}
                    </Pill>
                  ) : (
                    <Pill variant="amber">Pending</Pill>
                  )}
                </div>
              </div>
              {!placementRecordedPaid && (
                <button
                  style={s.btnGreen}
                  disabled={!leaseStartParts}
                  onClick={openMarkPaidModal}
                >
                  Mark placement paid
                </button>
              )}
            </div>
            <p style={{ fontSize: 12, color: "#9ca3af", marginTop: "0.75rem", marginBottom: 0 }}>
              {!leaseStartParts
                ? "Set a lease start date to mark placement commission as paid."
                : "Monthly rent after the first month is between landlord and tenant."}
            </p>
          </div>
        </div>
      )}

      {/* ── Landlord information ── */}
      {placementInfo.landlordInfo && (
        <div style={s.card}>
          <div style={s.cardHead}>Landlord information</div>
          <div style={s.cardBody}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
                gap: "0.875rem",
              }}
            >
              <FieldRow
                label="Landlord name"
                value={placementInfo.landlordInfo.landlordName || "Not specified"}
              />
              <FieldRow
                label="Contact"
                value={placementInfo.landlordInfo.landlordContact || "Not specified"}
              />
            </div>
          </div>
        </div>
      )}

      {/* ── Mark placement paid modal ── */}
      {markPaidModal && (
        <div
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
            display: "flex", alignItems: "center", justifyContent: "center",
            zIndex: 1060, padding: "1rem",
            fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
          }}
          onClick={() => { if (!submittingMarkPaid) setMarkPaidModal(false); }}
        >
          <div
            style={{ background: "#fff", borderRadius: 14, width: "100%", maxWidth: 420, boxShadow: "0 24px 64px rgba(0,0,0,0.18)", overflow: "hidden" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ padding: "1rem 1.25rem", borderBottom: "1px solid #e5e7eb", background: "#fafafa", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span style={{ fontSize: 15, fontWeight: 700, color: "#1a1d23" }}>Mark placement paid</span>
              <button style={{ background: "none", border: "none", cursor: "pointer", fontSize: 20, lineHeight: 1, color: "#6b7280" }} onClick={() => setMarkPaidModal(false)}>×</button>
            </div>

            <div style={{ padding: "1.25rem" }}>
              <div style={{ background: "#EFF6FF", border: "1px solid #BFDBFE", borderRadius: 8, padding: "9px 13px", marginBottom: "1.1rem", fontSize: 12, color: "#1E40AF", fontWeight: 500 }}>
                Placement commission — {formatCurrency(placementAmount)}
              </div>

              <div style={{ marginBottom: "0.875rem" }}>
                <label style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: "#6b7280", display: "block", marginBottom: 5 }}>Date received</label>
                <input
                  type="date"
                  value={markPaidDate}
                  onChange={(e) => setMarkPaidDate(e.target.value)}
                  style={{ fontSize: 13, padding: "7px 10px", border: "1px solid #d1d5db", borderRadius: 6, outline: "none", width: "100%", boxSizing: "border-box" }}
                  autoFocus
                />
              </div>

              <div style={{ marginBottom: "1rem" }}>
                <label style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: "#6b7280", display: "block", marginBottom: 5 }}>
                  Notes <span style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>(optional)</span>
                </label>
                <textarea
                  rows={2}
                  maxLength={500}
                  value={markPaidNotes}
                  onChange={(e) => setMarkPaidNotes(e.target.value)}
                  placeholder="e.g. Received via cheque"
                  style={{ width: "100%", fontSize: 13, padding: "7px 10px", border: "1px solid #d1d5db", borderRadius: 6, resize: "vertical", outline: "none", boxSizing: "border-box", fontFamily: "inherit" }}
                />
              </div>

              {markPaidError && (
                <div style={{ fontSize: 12, color: "#991B1B", background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 6, padding: "8px 12px", marginBottom: "0.75rem" }}>
                  {markPaidError}
                </div>
              )}
            </div>

            <div style={{ padding: "0.875rem 1.25rem", borderTop: "1px solid #e5e7eb", background: "#fafafa", display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button
                style={{ fontSize: 12, fontWeight: 500, padding: "6px 16px", borderRadius: 6, border: "1px solid #d1d5db", background: "transparent", color: "#374151", cursor: "pointer" }}
                onClick={() => setMarkPaidModal(false)}
                disabled={submittingMarkPaid}
              >
                Cancel
              </button>
              <button
                style={{ fontSize: 12, fontWeight: 600, padding: "6px 18px", borderRadius: 6, border: "none", background: "#1E40AF", color: "#fff", cursor: "pointer" }}
                onClick={handleSubmitMarkPaid}
                disabled={submittingMarkPaid}
              >
                {submittingMarkPaid ? "Saving…" : "Save payment"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OneTimeRentalFinancials;
